import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import PublicProfile from './pages/PublicProfile';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminPage from './pages/AdminPage';
import FanPage from './pages/FanPage';
import AffiliatePage from './pages/AffiliatePage';
import AffiliateDashboard from './pages/AffiliateDashboard';
import Pricing from './pages/Pricing';
import Billing from './pages/Billing';
import Campaigns from './pages/Campaigns';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import DMCA from './pages/DMCA';
import VerifyEmail from './pages/VerifyEmail';
import SeoLanding from './pages/SeoLanding';
import Examples from './pages/Examples';
import About from './pages/About';
import Reviews from './pages/Reviews';
import { seoPages } from './data/seoPages';
import { UpgradeModal } from './components/UpgradeModal';
import CookieBanner from './components/CookieBanner';
import { Toaster } from './components/ui/sonner';
import { api } from './utils/api';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginUser = (userData) => setUser(userData);

  const logoutUser = async () => {
    try { await api.logout(); } catch {}
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
      return userData;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, loginUser, logoutUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-white/15 border-t-white animate-spin" />
          <p className="text-xs uppercase tracking-[0.3em] text-white/40">Loading</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  const [upgrade, setUpgrade] = useState({ open: false, feature: 'this feature' });

  useEffect(() => {
    const handler = (e) => {
      const msg = e?.detail?.message || '';
      // Extract feature name from "X is a Pro feature..." message
      const match = msg.match(/^(.+?)\s+(?:is|are)\s+a\s+Pro\s+feature/i);
      const feature = match ? match[1].toLowerCase() : 'this feature';
      setUpgrade({ open: true, feature });
    };
    window.addEventListener('upgrade-required', handler);
    return () => window.removeEventListener('upgrade-required', handler);
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
        <UpgradeModal open={upgrade.open} feature={upgrade.feature} onClose={() => setUpgrade({ open: false, feature: 'this feature' })} />
        <CookieBanner />
        <Toaster position="top-right" />
      </AuthProvider>
    </Router>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-white/15 border-t-white animate-spin" />
          <p className="text-xs uppercase tracking-[0.3em] text-white/40">Loading</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to="/dashboard" replace /> : <Signup />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />} />
      <Route path="/affiliate" element={<AffiliatePage />} />
      <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/billing" element={<Billing />} />
      <Route path="/campaigns" element={<Campaigns />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/dmca" element={<DMCA />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/examples" element={<Examples />} />
      <Route path="/about" element={<About />} />
      <Route path="/reviews" element={<Reviews />} />
      {seoPages.map((p) => (
        <Route key={p.slug} path={`/${p.slug}`} element={<SeoLanding page={p} />} />
      ))}
      <Route path="/:username/fanpage" element={<FanPage />} />
      <Route path="/:username" element={<PublicProfile />} />
    </Routes>
  );
}

export default App;
